/**
 * QueuesPage — operational queues with counters, items, and quick actions.
 * Queue names + specs come from the backend; the page never hardcodes them.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Inbox, RefreshCw, ArrowRight } from 'lucide-react';
import { fetchQueues, fetchQueueItems, fetchStateMachines } from '@/services/workflowApi';
import StateBadge from '@/components/workflow/StateBadge';
import { stateLabelEs } from '@/components/workflow/StateBadge';
import OperationDetailDrawer from '@/components/workflow/OperationDetailDrawer';
import { queueLabelEs, opTypeLabelEs } from '@/components/workflow/labels';

const QueuesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [queues, setQueues] = useState({});
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateMachines, setStateMachines] = useState(null);
  const [drawerId, setDrawerId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, sm] = await Promise.all([fetchQueues(getAuthHeaders), fetchStateMachines(getAuthHeaders)]);
      setQueues(q.queues || {});
      setCounts(q.counts || {});
      setStateMachines(sm);
      const names = Object.keys(q.queues || {});
      if (names.length && !selected) setSelected(names[0]);
    } catch (e) {
      toast.error('No se pudieron cargar las colas');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selected]);

  const loadItems = useCallback(async () => {
    if (!selected) return;
    try {
      const r = await fetchQueueItems(getAuthHeaders, selected);
      setItems(r.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  }, [getAuthHeaders, selected]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadItems(); }, [loadItems]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-blue-600" />
            Cola Operacional
          </h1>
          <p className="text-xs text-slate-500">Filas de trabajo segregadas por etapa operativa</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { load(); loadItems(); }} data-testid="queues-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Object.entries(queues).map(([name, spec]) => (
          <button
            key={name}
            onClick={() => setSelected(name)}
            data-testid={`queue-card-${name}`}
            className={`text-left p-4 rounded-lg border-2 transition-all ${selected === name ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}
          >
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{opTypeLabelEs(spec.op_type)}</p>
            <p className="text-sm font-bold text-slate-900 mt-0.5">{queueLabelEs(name)}</p>
            <p className="text-[10px] text-slate-500 mt-1">{spec.description}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-2xl font-bold text-blue-600" data-testid={`queue-count-${name}`}>{counts[name] ?? 0}</span>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">{queueLabelEs(selected)} · {items.length}</p>
            <p className="text-[10px] text-slate-500">Estados: {(queues[selected]?.states || []).map(stateLabelEs).join(', ')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-3 text-left">ID</th>
                  <th className="p-3 text-left">Estado</th>
                  <th className="p-3 text-left">Monto</th>
                  <th className="p-3 text-left">Ruta</th>
                  <th className="p-3 text-left">Actualizado</th>
                  <th className="p-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-400">Cargando…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-400 italic">Cola vacía</td></tr>
                ) : items.map((o) => (
                  <tr key={o.id} className="border-t hover:bg-slate-50" data-testid={`queue-item-${o.id}`}>
                    <td className="p-3 font-mono text-[10px]">{o.id.slice(0, 12)}…</td>
                    <td className="p-3"><StateBadge state={o.state} /></td>
                    <td className="p-3">{o.amount ? `${o.amount} ${o.currency_from || ''}` : '—'}</td>
                    <td className="p-3">{o.route_origin && o.route_destination ? `${o.route_origin} → ${o.route_destination}` : '—'}</td>
                    <td className="p-3 text-slate-500">{new Date(o.updated_at).toLocaleString()}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setDrawerId(o.id)} data-testid={`queue-open-${o.id}`}>Abrir</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {drawerId && (
        <OperationDetailDrawer
          operationId={drawerId}
          stateMachines={stateMachines}
          onClose={() => setDrawerId(null)}
          onChanged={loadItems}
        />
      )}
    </div>
  );
};

export default QueuesPage;
